/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import React, { useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import Logo from 'public/logos/full-color-reversed/logo.svg';
import Text from 'public/logos/full-color-reversed/text.svg';
import { NavItem } from 'types/nav';

import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { NavIcons, System } from '../icons';

export const SideNav = () => {
  const pathname = usePathname();
  const { locale, messages } = useLanguage();
  const [isCollapsed, setIsCollapsed] = useState(true);
  // track timeout for collapsing sidebar with delay
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsCollapsed(false);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsCollapsed(true);
    }, 200); // delay to prevent flicker
  };

  const sidebar: NavItem[] = [
    {
      id: 0,
      title: messages?.General?.applications,
      href: '/apps',
      icon: NavIcons.AppIcon(),
    },
    {
      id: 1,
      title: messages?.General?.services,
      href: '/services',
      icon: NavIcons.ServicesIcon(),
    },
    {
      id: 2,
      title: messages?.Sidebar?.providers,
      href: '/providers',
      icon: NavIcons.ProvidersIcon(),
    },
    {
      id: 6,
      title: messages?.Sidebar?.eval_templates,
      href: '/eval-templates',
      icon: NavIcons.EvalTemplate(),
    },
    {
      id: 3,
      title: messages?.Sidebar?.usage,
      href: '/usage',
      icon: NavIcons.UsageIcon(),
    },
    {
      id: 4,
      title: messages?.General?.settings,
      href: '/settings',
      icon: System.SettingsIcon(),
    },
    {
      id: 5,
      title: messages?.Sidebar?.documentation,
      href: 'https://docs.okahu.ai/',
      icon: NavIcons.DocumentationIcon(),
      external: true,
    },
  ];

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-50 h-full border-r border-[#FFFFFF0F] bg-canvas-background-secondary transition-all duration-300 hover:shadow-[4px_4px_16px_0px_#0000003D]',
        isCollapsed
          ? 'w-[70px] [transition-timing-function:cubic-bezier(0.5,-0.5,0.5,1.3)]'
          : 'w-[224px] [transition-timing-function:cubic-bezier(0.5,-0.3,0.2,1.3)]'
      )}
      data-testid="side-nav"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="mt-4 flex items-center ">
        <Link
          href={`/${locale}/apps`}
          className="pointer-events-none flex items-center"
        >
          <Image
            src={Logo}
            alt={messages?.Logo?.logo_alt ?? 'Logo'}
            className={isCollapsed ? 'ml-6' : 'ml-6'}
          />
          <Image
            src={Text}
            alt={messages?.Logo?.logo_alt ?? 'Text Logo'}
            className={cn(
              'ml-3 transition-opacity duration-200',
              isCollapsed ? 'opacity-0' : 'opacity-100'
            )}
          />
        </Link>
      </div>
      <div className="mb-5 mt-1 space-y-[1px]">
        {sidebar.map((item) => {
          const isActive =
            item.id === 0
              ? pathname.startsWith(`/${locale}/apps`) ||
                pathname.startsWith(`/${locale}/components`)
              : pathname.startsWith(
                  item.id !== 5 ? `/${locale}${item.href}` : item.href
                );

          return (
            <React.Fragment key={item.id}>
              {item.tooltip ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        target={item.external ? '_blank' : '_self'}
                        href={
                          item.id !== 5 ? `/${locale}${item.href}` : item.href
                        }
                        className={cn(
                          'mt-3 flex h-10 items-center rounded-sm text-body',
                          isCollapsed
                            ? 'mx-2 w-[54px] justify-center'
                            : 'mx-4 w-[200px] gap-x-3 pl-4',
                          pathname.startsWith(
                            item.id !== 5 ? `/${locale}${item.href}` : item.href
                          )
                            ? 'bg-gradient-sky-dark'
                            : ''
                        )}
                      >
                        <span className="text-icon-fill">{item.icon}</span>
                        {!isCollapsed && <p>{item.title}</p>}
                        {item.wip && !isCollapsed && (
                          //TODO refactor arbitrary classes after design revamp
                          <span className="-ml-0.5 rounded bg-[#EFDA1F]/[.12] px-1.5 text-btn-label-sm text-[#EFDA1F]">
                            {messages?.General?.wip}
                          </span>
                        )}
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="max-w-52 rounded border-transparent bg-[#07181E]"
                    >
                      {item.tooltip}
                      <TooltipArrow className="fill-[#07181E]" />
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Link
                  // id is necessary for tour-guide
                  id={'guide' + item.id.toString()}
                  target={item.external ? '_blank' : '_self'}
                  href={item.id !== 5 ? `/${locale}${item.href}` : item.href}
                  className={cn(
                    'mt-3 flex h-10 items-center rounded-[4px] text-nav-text-default',
                    isCollapsed
                      ? 'mx-2 w-[54px] justify-center'
                      : 'mx-3 w-[200px] gap-x-3 pl-[14px]',
                    isActive ? 'bg-gradient-sky-dark-opacity' : ''
                  )}
                >
                  <span
                    className={cn(
                      'text-icon-fill',
                      isActive ? 'text-white' : ''
                    )}
                  >
                    {item.icon}
                  </span>
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-500',
                      isCollapsed ? 'w-0' : 'w-auto'
                    )}
                  >
                    <p
                      className={cn(
                        'text-nav-link-active transition-opacity duration-500',
                        isActive ? 'text-nav-text-selected' : '',
                        isCollapsed ? 'opacity-0' : 'opacity-100'
                      )}
                    >
                      {item.title}
                    </p>
                  </div>

                  {item.wip && !isCollapsed && (
                    //TODO refactor arbitrary classes after design revamp
                    <span className="-ml-0.5 rounded bg-[#EFDA1F]/[.12] px-1.5 text-btn-label-sm text-[#EFDA1F]">
                      {messages?.General?.wip}
                    </span>
                  )}
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </aside>
  );
};
